import { Search, ArrowUp, ArrowDown, CornerDownLeft } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores/ui';

interface SearchResult {
  type: 'application' | 'page';
  id?: string;
  title: string;
  subtitle?: string;
  path: string;
}

const staticPages: SearchResult[] = [
  { type: 'page', title: 'Home', subtitle: 'Account home', path: '/' },
  { type: 'page', title: 'Applications', subtitle: 'View all applications', path: '/applications' },
  { type: 'page', title: 'API Keys', subtitle: 'Manage API keys', path: '/keys' },
];

export function QuickSearch() {
  const { quickSearchOpen, setQuickSearchOpen } = useUIStore();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  const results = query
    ? staticPages.filter(
        (p) =>
          p.title.toLowerCase().includes(query.toLowerCase()) ||
          p.subtitle?.toLowerCase().includes(query.toLowerCase())
      )
    : staticPages;

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setQuickSearchOpen(!quickSearchOpen);
        return;
      }

      if (!quickSearchOpen) return;

      if (e.key === 'Escape') {
        setQuickSearchOpen(false);
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        const selected = results[selectedIndex];
        if (selected) {
          navigate(selected.path);
          setQuickSearchOpen(false);
          setQuery('');
        }
        return;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [quickSearchOpen, setQuickSearchOpen, results, selectedIndex, navigate]);

  const handleSelect = useCallback(
    (result: SearchResult) => {
      navigate(result.path);
      setQuickSearchOpen(false);
      setQuery('');
    },
    [navigate, setQuickSearchOpen]
  );

  if (!quickSearchOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        onClick={() => setQuickSearchOpen(false)}
      />

      <div className="fixed left-1/2 top-1/4 z-50 w-full max-w-lg -translate-x-1/2 rounded-component border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-black">
        <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <Search className="h-5 w-5 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search applications, pages..."
            className="flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 dark:text-white"
            autoFocus
          />
          <kbd className="rounded border border-slate-200 bg-surface-hover px-1.5 py-0.5 text-xs text-slate-400 dark:border-slate-600 dark:bg-surface-hover-dark">
            Esc
          </kbd>
        </div>

        {!query && (
          <div className="border-b border-slate-200 px-4 py-2 dark:border-slate-700">
            <p className="text-xs text-slate-500">Search tips</p>
            <div className="mt-1 space-y-0.5 text-xs text-slate-400">
              <p>
                <code className="text-accent-500">apps:</code> — Search Applications
              </p>
              <p>
                <code className="text-accent-500">runs:</code> — Search Runs
              </p>
            </div>
          </div>
        )}

        <div className="max-h-80 overflow-y-auto py-2">
          {results.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-500">No results found</p>
          ) : (
            results.map((result, index) => (
              <button
                key={result.path}
                onClick={() => handleSelect(result)}
                className={cn(
                  'flex w-full items-center gap-3 px-4 py-2 text-left',
                  index === selectedIndex
                    ? 'bg-surface-selected text-slate-900 dark:bg-surface-selected-dark dark:text-white'
                    : 'text-slate-700 hover:bg-surface-hover dark:text-slate-300 dark:hover:bg-surface-hover-dark'
                )}
              >
                <div className="flex-1">
                  <p className="text-sm font-medium">{result.title}</p>
                  {result.subtitle && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">{result.subtitle}</p>
                  )}
                </div>
                {index === selectedIndex && (
                  <CornerDownLeft className="h-4 w-4 text-slate-400" />
                )}
              </button>
            ))
          )}
        </div>

        <div className="flex items-center gap-4 border-t border-slate-200 px-4 py-2 text-xs text-slate-400 dark:border-slate-700">
          <span className="flex items-center gap-1">
            <ArrowUp className="h-3 w-3" />
            <ArrowDown className="h-3 w-3" />
            to navigate
          </span>
          <span className="flex items-center gap-1">
            <CornerDownLeft className="h-3 w-3" />
            to select
          </span>
        </div>
      </div>
    </>
  );
}
